/**
 * Boho Magic — fbc/fbp click-ID -> cart attribute bridge
 *
 * WHY: Shopify's checkout custom-pixel sandbox (bm-checkout-pixel.js) cannot
 * read the storefront's _fbc/_fbp cookies directly (different origin/sandbox
 * from the storefront). theme.liquid runs in the normal page context and CAN
 * read/set cookies, so this script:
 *   1. Reads the existing _fbc/_fbp cookies (set by Meta's own pixel/ads
 *      click, if present on this domain).
 *   2. If a fresh `fbclid` is present in the URL (i.e. the visitor just
 *      clicked a Meta ad), builds/refreshes the _fbc cookie in Meta's
 *      documented format, since the native cookie may not exist yet on a
 *      brand-new click.
 *   3. Pushes whichever values exist into Shopify's cart-level `attributes`
 *      via POST /cart/update.js.
 *
 * Cart attributes carry through to checkout.attributes, which IS readable by
 * the checkout pixel sandbox — bm-checkout-pixel.js reads them from there and
 * forwards them to the GTM server container as x-fb-ck-fbc / x-fb-ck-fbp,
 * where the Meta CAPI tag maps them to the fbc/fbp match-quality keys.
 *
 * 2026-09-22 FIX: Meta Events Manager showed our fbc/fbp coverage running
 * well below Shopify's native Facebook & Instagram channel (20.73%/23.17%
 * vs. 60.98% fbc on native, checked same day). Root cause hypothesis: this
 * script previously ran its cookie check exactly once, at initial script
 * execution (on `defer`, i.e. right after DOMContentLoaded). If Meta's own
 * pixel (which sets _fbp, and _fbc on some flows) hasn't finished setting
 * its cookie by that single moment — because it loads asynchronously via a
 * tag manager, or the ad-click redirect chain is still settling — we missed
 * it for that entire pageview and only had another chance on the visitor's
 * NEXT full pageload. A visitor who lands on one page and goes straight to
 * checkout never gave us a second chance. Fix: keep the original immediate
 * check (unchanged behavior when cookies are already present), and add a
 * short series of retries over the following few seconds to catch a
 * slightly-late cookie within the SAME pageview. Each retry reuses the same
 * bmSyncFbClickIds() function, which already only POSTs when the value
 * actually changed (see bmFbSynced below) — so this adds no extra network
 * calls when nothing changes between checks.
 *
 * See project doc: gtm-server-side-tracking-plan.md — "fbc/fbp cookie
 * bridge" section.
 *
 * Namespaced as `bm*` / `BM_*` to avoid clashing with theme or app scripts.
 */
(function () {
  'use strict';

  function bmGetCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function bmSetCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  function bmGetUrlParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (e) {
      return null;
    }
  }

  // Meta's documented _fbc format: fb.<subdomain_index>.<creation_time_ms>.<fbclid>
  // subdomain_index is 1 for the top-level domain, which covers this store.
  function bmBuildFbc(fbclid) {
    return 'fb.1.' + Date.now() + '.' + fbclid;
  }

  function bmSyncFbClickIds() {
    var fbclid = bmGetUrlParam('fbclid');
    var fbc = bmGetCookie('_fbc');

    // A fresh fbclid on the URL always wins (most recent ad click), even if
    // an older _fbc cookie already exists from a previous visit — UNLESS
    // we already minted an _fbc cookie for this exact fbclid a moment ago
    // (on the immediate check or an earlier retry within this same
    // pageview). bmBuildFbc() embeds Date.now() in the value, so rebuilding
    // it on every retry for an unchanged fbclid would produce a "new"
    // looking fbc each time, defeat the syncKey dedup below, and fire a
    // redundant /cart/update.js POST on every one of the 3 retries.
    // Caught by the harness in bridge_test/ before this shipped — see
    // Scenario A there.
    var suffix = '.' + fbclid;
    var alreadyHasThisClick = fbc && fbc.slice(-suffix.length) === suffix;
    if (fbclid && !alreadyHasThisClick) {
      fbc = bmBuildFbc(fbclid);
      bmSetCookie('_fbc', fbc, 90);
    }

    var fbp = bmGetCookie('_fbp');

    // Nothing to send yet (no ad click this session, and Meta's own pixel
    // hasn't set _fbp) — nothing to do. A later retry (see bottom of file)
    // will pick it up if either cookie appears shortly after this check.
    if (!fbc && !fbp) {
      return;
    }

    // Avoid re-POSTing identical values on every single check/page view.
    var syncKey = (fbc || '') + '|' + (fbp || '');
    var lastSynced = null;
    try {
      lastSynced = sessionStorage.getItem('bmFbSynced');
    } catch (e) {
      // Storage can throw in some private-browsing modes — fall through
      // and just sync anyway rather than breaking.
    }
    if (lastSynced === syncKey) {
      return;
    }

    // IMPORTANT: Shopify's POST /cart/update.js REPLACES the entire
    // `attributes` object — it does not merge. Blindly POSTing just
    // {fbc, fbp} would silently wipe out any other cart attribute set by
    // another app or feature (a delivery-date picker, a gift-note app,
    // etc.), even though nothing in this theme's own code uses cart
    // attributes today (confirmed 2026-09-18 — engraving uses line-item
    // properties, cart notes use the separate `note` field). Reading the
    // current cart first and merging is cheap insurance against a future
    // app doing so.
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var mergedAttributes = {};
        var existing = (cart && cart.attributes) || {};
        for (var key in existing) {
          if (Object.prototype.hasOwnProperty.call(existing, key)) {
            mergedAttributes[key] = existing[key];
          }
        }
        if (fbc) mergedAttributes.fbc = fbc;
        if (fbp) mergedAttributes.fbp = fbp;

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: mergedAttributes })
        });
      })
      .then(function () {
        try {
          sessionStorage.setItem('bmFbSynced', syncKey);
        } catch (e) {
          /* non-fatal */
        }
      })
      .catch(function (err) {
        // Tracking must never break the storefront.
        if (window.console && console.warn) {
          console.warn('[bm-fbclid-fbp-bridge] cart attribute sync failed', err);
        }
      });
  }

  function bmSafeSync() {
    try {
      bmSyncFbClickIds();
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('[bm-fbclid-fbp-bridge] unexpected error', e);
      }
    }
  }

  // Immediate check — unchanged from the original behavior, covers the
  // common case where Meta's pixel (or the fbclid URL param) already has
  // the cookie set by the time this script runs.
  bmSafeSync();

  // Retry a few times over the next several seconds to catch a cookie that
  // gets set asynchronously shortly after page load (e.g. a tag-manager-
  // loaded Meta Pixel that hasn't finished initializing yet). Each retry is
  // a cheap no-op if nothing changed since the last successful sync.
  [800, 2000, 4000].forEach(function (delayMs) {
    setTimeout(bmSafeSync, delayMs);
  });
})();
